<script>
import { INDICATIONS_TYPES } from "@/services/constants";
import { getDoctorLabel } from "@/utils/names";
import configService from "@/services/config";
import { getProgramStatusText } from "@/services/program-status";

export default {
  name: "IndicationCard",
  props: {
    indication: { type: Object, required: true },
    canEdit: { type: Boolean, default: false },
    isTypeAllowed: { type: Boolean, default: false },
    editTimeLimitInMinutes: { type: Number, default: null },
    canResend: { type: Boolean, default: false },
    doctor: { type: Object, default: null },
    hideDoctorInfo: { type: Boolean, default: false },
  },

  data() {
    return {
      s3Url: configService.s3Url,
      labDescriptions: [],
      imageDescriptions: [],
    };
  },

  computed: {
    indicationMeta() {
      return (
        INDICATIONS_TYPES.find((i) => i.type === this.indication.type) || {}
      );
    },
    doctorInfo() {
      return getDoctorLabel(this.indication.doctor || this.doctor);
    },
    canResendIndication() {
      const resendableTypes = [
        "medications",
        "lab",
        "images",
        "practices",
        "certificates",
        "notes",
      ];
      return this.canResend && resendableTypes.includes(this.indication.type);
    },
    editButtonTooltip() {
      if (this.canEdit) {
        return "";
      }

      if (this.editTimeLimitInMinutes && this.indication.createdAt) {
        const createdAt = new Date(this.indication.createdAt);
        const now = new Date();
        const diffInMinutes = (now - createdAt) / (1000 * 60);

        if (diffInMinutes > this.editTimeLimitInMinutes) {
          return `Solo pueden editarse indicaciones hasta ${this.editTimeLimitInMinutes} minutos desde la creación`;
        }
      }

      return "";
    },
  },

  async created() {
    await this.loadLabDescriptions();
    await this.loadImageInfo();
  },
  methods: {
    getProgramStatusText,
    async loadLabDescriptions() {
      if (!this.indication.labs || !Array.isArray(this.indication.labs)) {
        this.labDescriptions = [];
        return;
      }

      // Asumir que siempre vienen populados
      this.labDescriptions = this.indication.labs.map(
        (lab) => lab?.description || "Sin descripción"
      );
    },

    loadImageInfo() {
      if (!this.indication.image) {
        this.imageDescriptions = ["No se le asignó una imagen"];
        return;
      }

      const images = Array.isArray(this.indication.image)
        ? this.indication.image
        : [this.indication.image];

      this.imageDescriptions = images.map((image) => {
        const description = image?.description || "Sin descripción";
        return image?.category
          ? `${image.category} - ${description}`
          : description;
      });
    },

    getDiagnosisDescription(ind) {
      return (
        (ind.diagnosis && ind.diagnosis.description) ||
        (ind.diagnosis && ind.diagnosis.name) ||
        null
      );
    },
    getMedicationName(ind) {
      return ind.medication?.name || ind.publicMedication?.name || "Sin nombre";
    },
    getFileName(key) {
      return key?.split("/").pop() || "";
    },
    formatQuestionAnswer(question) {
      if (!question.selected || question.selected.length === 0) return "-";

      switch (question.type) {
        case "checkbox":
          return Array.isArray(question.selected)
            ? question.selected.join(", ")
            : question.selected;
        case "select":
        case "radio":
        case "input":
        case "inputNumber":
        case "textarea":
        case "date":
        default:
          return question.selected;
      }
    },
  },
};
</script>

<template lang="pug">
.cardContainer
  .titleContainer
    micon(:name="indicationMeta.micon")
    span.title {{ indicationMeta.name }}
    button.button-glass-new.small(
      v-if="isTypeAllowed"
      :disabled="!canEdit"
      v-tooltip="editButtonTooltip"
      @click="$emit('edit-evolution', indication)"
    ) Editar
    span.doctor-info(v-if="doctorInfo && !hideDoctorInfo") {{ doctorInfo }}

  .bodyContainer

    //Evolucion
    template(v-if="indication.type === 'evolution'")
      p(v-if="getDiagnosisDescription(indication)")
        strong.label-indication Diagnóstico:
        | {{ getDiagnosisDescription(indication) }}
      p(v-if="indication.publicDiagnosis")
        strong.label-indication Diagnóstico manual:
        | {{ indication.publicDiagnosis }}
      p(v-if="indication?.publicConsultation || indication?.consultation")
        strong.label-indication Motivo de consulta:
        | {{ indication.publicConsultation || indication.consultation }}
      p(v-if="indication.description") {{ indication.description }}

    // Medicamentos
    template(v-if="indication.type === 'medications'")
      p(v-if="getDiagnosisDescription(indication)")
        strong.label-indication Diagnóstico:
        | {{ getDiagnosisDescription(indication) }}
      p(v-if="indication.publicDiagnosis")
        strong.label-indication Diagnóstico manual:
        | {{ indication.publicDiagnosis }}
      .important {{ getMedicationName(indication) }}
      p(v-if="indication.createdAt")
        strong.label-indication Fecha:
        | {{ indication.createdAt | formatDate }}
      p(v-if="indication.dosage || indication.frequency || indication.duration")
        strong.label-indication Posología e indicaciones:
        | Tomar {{ indication.quantity || '-' }} cada {{ indication.frequency || '-' }} hs por {{ indication.duration || '-' }} días.
      p(v-if="indication.observations")
        strong.label-indication Notas adicionales:
        | {{ this.indication.observations }}

    // Imágenes
    template(v-else-if="indication.type === 'images'")
      p(v-if="getDiagnosisDescription(indication)")
        strong.label-indication Diagnóstico:
        | {{ getDiagnosisDescription(indication) }}
      p(v-if="indication.publicDiagnosis")
        strong.label-indication Diagnóstico manual:
        | {{ indication.publicDiagnosis }}
      .important(v-for="desc in imageDescriptions" :key="desc") {{ desc }}
      p(v-if="indication.observation")
        strong.label-indication Observaciones:
        | {{ indication.observation }}

    // Laboratorios
    template(v-else-if="indication.type === 'lab'")
      p(v-if="getDiagnosisDescription(indication)")
        strong.label-indication Diagnóstico:
        | {{ getDiagnosisDescription(indication) }}
      p(v-if="indication.publicDiagnosis")
        strong.label-indication Diagnóstico manual:
        | {{ indication.publicDiagnosis }}
      .important(v-for="desc in labDescriptions" :key="desc") {{ desc }}
      p(v-if="indication.description")
        strong.label-indication Descripción:
        | {{ indication.description }}
      p(v-if="indication.observation")
        strong.label-indication Observaciones:
        | {{ indication.observation }}

    // Practicas
    template(v-else-if="indication.type === 'practices'")
      p(v-if="getDiagnosisDescription(indication)")
          strong.label-indication Diagnóstico:
          | {{ getDiagnosisDescription(indication) }}
      p(v-if="indication.publicDiagnosis")
        strong.label-indication Diagnóstico manual:
        | {{ indication.publicDiagnosis }}
      p(v-if="indication.description")
        strong.label-indication Descripción:
        | {{ indication.description }}

    // Resultados de imágenes
    template(v-else-if="indication.type === 'imagesResults'")
      p(v-if="getDiagnosisDescription(indication)")
        strong.label-indication Diagnóstico:
        | {{ getDiagnosisDescription(indication) }}
      p.report-text(v-if="indication.description")
        strong.label-indication Descripción:
        | {{ indication.description }}

    // Resultados de laboratorio
    template(v-else-if="indication.type === 'labResults'")
      p(v-if="getDiagnosisDescription(indication)")
        strong.label-indication Diagnóstico:
        | {{ getDiagnosisDescription(indication) }}
      p.report-text(v-if="indication.description")
        strong.label-indication Descripción:
        | {{ indication.description }}

    // Certificados y Notas
    template(v-else-if="['certificates','notes'].includes(indication.type)")
      p(v-if="getDiagnosisDescription(indication)")
        strong.label-indication Diagnóstico:
        | {{ getDiagnosisDescription(indication) }}
      p(v-if="indication.publicDiagnosis")
        strong.label-indication Diagnóstico manual:
        | {{ indication.publicDiagnosis }}
      p(v-if="indication.description")
        strong.label-indication Motivo:
        | {{ indication.description }}
      p(v-if="indication.observation")
        strong.label-indication Descripción:
        | {{ indication.observation }}

    // Formularios
    template(v-else-if="indication.type === 'forms'")
      .form-container(v-if="indication.isForm")
        .form-results(v-if="indication.sections")
          .form-section(v-for="section in indication.sections" :key="section.name")
            h4.section-title {{ section.name }}
            .form-questions
              .question-item(v-for="question in section.questions" :key="question.name")
                .question-field
                  label.question-label {{ question.name }}
                  .question-answer {{ formatQuestionAnswer(question) }}
                  .observation-field(v-if="question.observationEnable && question.observation")
                    label.observation-label Especifique:
                    span.observation-text {{ question.observation }}
        
        // Archivos adjuntos del formulario (si hay)
        ul.file-links(v-if="indication.fileSection && indication.fileSection.files && indication.fileSection.files.length")
          li(v-for="file in indication.fileSection.files" :key="file.key")
            strong.label-indication Archivo adjunto:
            a(:href="s3Url + '/' + file.key" target="_blank") {{ getFileName(file.key) }}

    // Programas
    template(v-else-if="indication.type === 'program'")
      .form-container(v-if="indication.isProgram")
        .program-header
          .program-name {{ indication.name }}
          .program-status(:class="indication.status") {{ getProgramStatusText(indication.status) }}
        .program-description(v-if="indication.description") {{ indication.description }}
        
        .form-results(v-if="indication.initialData && indication.initialData.length")
          h4.section-title Formularios del programa:
          .form-section(v-for="form in indication.initialData" :key="form._id")
            h4.section-title {{ form.name }}
            .form-description(v-if="form.description") {{ form.description }}
            
            .form-questions(v-if="form.sections && form.sections.length")
              .question-item(v-for="section in form.sections" :key="section._id")
                .question-field
                  label.question-label {{ section.name }}
                  .question-answer(v-if="section.questions && section.questions.length")
                    .section-questions
                      .question-item(v-for="question in section.questions" :key="question._id")
                        .question-field
                          label.question-label {{ question.name }}
                          .question-answer(v-if="question.selected") {{ formatQuestionAnswer(question) }}
            
            // Archivos adjuntos del formulario (si hay)
            ul.file-links(v-if="form.fileSection && form.fileSection.files && form.fileSection.files.length")
              li(v-for="file in form.fileSection.files" :key="file.key")
                strong.label-indication Archivo adjunto:
                a(:href="s3Url + '/' + file.key" target="_blank") {{ getFileName(file.key) }}

    // Archivos adjuntos (si hay)
    ul.file-links(v-if="indication.files && indication.files.length")
      li(v-for="file in indication.files" :key="file.key")
        strong.label-indication Archivo adjunto:
        a(:href="s3Url + '/' + file.key" target="_blank") {{ file.originalName || getFileName(file.key) }}

    .resend-row(v-if="canResendIndication")
      button.button-glass-new.small(@click.stop="$emit('print-prescription', indication)") Imprimir
      button.button-glass-new.small(@click.stop="$emit('resend-prescription', indication)") Reenviar

</template>

<style scoped>
.cardContainer {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
  border: solid 2px #d5dbf3;
  border-radius: 10px;
  background-color: #fff;
}

.titleContainer {
  padding: 6px 12px;
  font-weight: 600;
  font-size: 15px;
  border-bottom: 2px solid #d5dbf3;
  display: flex;
  align-items: center;
  gap: 8px;
}

.doctor-info {
  margin-left: auto;
  font-size: 11px;
  font-weight: 400;
  color: #6b7aad;
}

.small {
  font-size: 11px;
  padding: 4px 8px;
  height: auto;
  min-height: 24px;
}

.bodyContainer {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 7px 10px;
  font-size: 14px;
}

.important {
  background-color: #d5dbf3;
  color: #1e3270;
  font-weight: 600;
  border-radius: 10px;
  font-size: 10px;
  padding: 6px 10px;
  margin-bottom: 5px;
}

p {
  margin: 0;
}

.label-indication {
  font-weight: 600;
  margin-right: 4px;
  display: inline;
}

.resend-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.file-links {
  margin-top: 8px;
}

.file-links li {
  margin-bottom: 4px;
}

.file-links a {
  color: #3959b6;
  text-decoration: underline;
  cursor: pointer;
}
.file-links a:hover {
  text-decoration: none;
}

.form-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-info {
  display: flex;
  align-items: center;
  gap: 4px;
}

.form-results {
  margin-top: 10px;
}

.form-section {
  margin-bottom: 15px;
  padding: 10px;
  border-radius: 6px;
}

.section-title {
  margin: 0 0 10px 0;
  color: #1e3270;
  font-size: 14px;
  font-weight: 600;
}

.form-questions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.question-item {
  background-color: white;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.question-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.question-label {
  font-weight: 600;
  color: #495057;
  font-size: 13px;
}

.question-answer {
  color: #212529;
  font-size: 13px;
  padding-left: 0px;
  border-left: 0px;
}

.observation-field {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #efe9e9;
}

.observation-label {
  font-weight: 500;
  color: #6c757d;
  font-size: 12px;
  margin-right: 4px;
}

.observation-text {
  color: #495057;
  font-size: 12px;
  font-style: italic;
}

.program-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e0e0e0;
}

.program-name {
  font-weight: 600;
  color: #1e3270;
  font-size: 14px;
}

.program-status {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  text-transform: capitalize;

  &.pending {
    background-color: #fef3c7;
    color: #92400e;
    border: 1px solid #fcd34d;
  }

  &.active {
    background-color: #d1fae5;
    color: #065f46;
    border: 1px solid #6ee7b7;
  }

  &.completed {
    background-color: #dbeafe;
    color: #1e40af;
    border: 1px solid #93c5fd;
  }

  &.cancelled {
    background-color: #fee2e2;
    color: #991b1b;
    border: 1px solid #fca5a5;
  }
}

.program-description {
  color: #666;
  font-size: 13px;
  margin-bottom: 10px;
  line-height: 1.4;
}

.section-questions {
  margin-top: 8px;
}

.report-text {
  white-space: pre-line;
  word-wrap: break-word;
  line-height: 1.5;
}
</style>
